import React from 'react';
import CustomIcon from '../../../../Common/CustomIcon/CustomIcon';
import style from './AddContentModal.module.css';
import {
	toCamelCase,
} from 'Crow/Common/Crow';
import {
	nonAddableVCLProviderKeys,
} from '../../../VCLSettings';

/**
 * Modal component for adding new content providers
 * Used for both regular content and backfill content
 */
const AddContentModal = ({
	isOpen,
	onClose,
	addContentType,
	isLoadingSlideDecks,
	availableSlideDecks,
	availablePublishedFeeds = [],
	onAddProvider,
	account, // New prop for account data with provider configuration
}) => {
	if (!isOpen) return null;

	// Parse enabled providers from account configuration
	const getEnabledProviders = () => {
		if (!account?.visual_content_loop_providers) {
			console.log('No visual_content_loop_providers found in account data');
			return [];
		}

		try {
			const config = JSON.parse(account.visual_content_loop_providers);
			console.log('Parsed VCL provider config:', config);
			const enabledProviders = [];

			// Helper function to process provider sections
			const processProviderSection = (sectionConfig, categoryName) => {
				if (sectionConfig && typeof sectionConfig === 'object') {
					Object.keys(sectionConfig).forEach((providerKey) => {
						// feature flags share this blob with real feeds — an
						// enabled flag is not an addable provider
						if (nonAddableVCLProviderKeys.includes(providerKey)) {
							return;
						}

						const providerConfig = sectionConfig[providerKey];
						if (providerConfig && providerConfig.enabled === true) {
							// Create a human-readable label from the provider key
							const label = providerKey
								.split('_')
								.map((word) => {
									return word.charAt(0).toUpperCase() + word.slice(1);
								})
								.join(' ');

							enabledProviders.push({
								key:      providerKey,
								label,
								type:     providerKey,
								category: categoryName,
								options:  providerConfig.options || {},
							});
						}
					});
				}
			};

			// Process all provider sections
			const providerSections = [
				{
					section:  config.advertising,
					category: 'Advertising',
				},
				{
					section:  config.infotainment,
					category: 'Infotainment',
				},
			];

			providerSections.forEach(({
				section, category,
			}) => {
				processProviderSection(section, category);
			});

			console.log('Enabled providers:', enabledProviders);
			return enabledProviders;
		} catch (error) {
			console.warn('Failed to parse visual_content_loop_providers:', error);
			return [];
		}
	};

	const enabledProviders = getEnabledProviders();

	return (
		<div className={style.modalOverlay} onClick={onClose}>
			<div className={style.modalContainer} onClick={(e) => {
				return e.stopPropagation();
			}}>
				<div className={style.modalHeader}>
					<h2 className={style.modalTitle}>
						Add {addContentType === 'content' ? 'Content' : 'Backfill'}
					</h2>
					<button
						className={style.modalCloseButton}
						onClick={onClose}
						title="Close"
					>
						<CustomIcon icon="times" />
					</button>
				</div>
				<div className={style.modalBody}>
					{isLoadingSlideDecks && (
						<div className={style.loadingIndicator}>
							<CustomIcon icon="spinner" spin />
							<span>Loading available slide decks...</span>
						</div>
					)}

					{!isLoadingSlideDecks && (
						<>
							{/* Slide Decks Section */}
							{availableSlideDecks.length > 0 && (
								<>
									<div className={style.modalSectionTitle}>Available Slide Decks:</div>
									{availableSlideDecks.map((slideDeck) => {
										return (
											<button
												key={slideDeck.id}
												className={style.modalOption}
												onClick={() => {
													return onAddProvider(slideDeck);
												}}
											>
												<span className={style.modalOptionName} data-test-id="modalOptionName">{slideDeck.name}</span>
											</button>
										);
									})}
								</>
							)}

							{/* Published Feeds Section (DEV-11955) */}
							{availablePublishedFeeds.length > 0 && (
								<>
									<div className={style.modalSectionTitle}>Published Feeds:</div>
									{availablePublishedFeeds.map((feed) => {
										return (
											<button
												key={`published_feed-${feed.id}`}
												className={style.modalOption}
												onClick={() => {
													return onAddProvider(feed);
												}}
											>
												<span className={style.modalOptionName} data-test-id="modalOptionName">
													{feed.name}
													{feed.source_account_name ? ` (${feed.source_account_name})` : ''}
												</span>
											</button>
										);
									})}
								</>
							)}

							{/* Additional Provider Types Section - Organized by Category */}
							{enabledProviders.length > 0 && (() => {
							// Group providers by category
								const providersByCategory = enabledProviders.reduce((acc, provider) => {
									const category = provider.category || 'Other';
									if (!acc[category]) {
										acc[category] = [];
									}
									acc[category].push(provider);
									return acc;
								}, {});

								return (
									<>
										{Object.keys(providersByCategory).sort().map((category) => {
											return (
												<React.Fragment key={category}>
													<div className={style.modalSectionTitle}>
														{category}:
													</div>
													{providersByCategory[category].map((provider) => {
														return (
															<button
																key={provider.type}
																className={style.modalOption}
																onClick={() => {
																	return onAddProvider({
																		id:       `${provider.type}-${Date.now()}`,
																		type:     provider.type,
																		category: provider.category,
																		options:  provider.options,
																	});
																}}
															>
																<span className={style.modalOptionName} data-test-id="modalOptionName">
																	{provider.label}
																</span>
															</button>
														);
													})}
												</React.Fragment>
											);
										})}
									</>
								);
							})()}							{/* No Options Available */}
							{availableSlideDecks.length === 0 && availablePublishedFeeds.length === 0 && enabledProviders.length === 0 && (
								<div className={style.noSlideshowsMessage}>
									No content providers available. Contact your administrator to enable additional content providers.
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default AddContentModal;